/**
 * Typed route caller (rebuild Phase 7) — the client half of `defineRoute`.
 *
 * A page declares its API surface once, deriving every shape from the SERVER's
 * route definitions via type-only imports (erased at build — no server code can
 * reach the browser bundle):
 *
 *   import type { notesRouteDefs } from "../server/routes";
 *   import { typedRoute } from "@d12labs/client";
 *
 *   export const notesApi = {
 *     list: typedRoute<typeof notesRouteDefs.list>()("GET", "/api/plugins/notes/items"),
 *     update: typedRoute<typeof notesRouteDefs.update>()("PATCH", "/api/plugins/notes/items/:id"),
 *   };
 *
 *   await notesApi.update({ params: { id }, input: { title } });  // ← all typed
 *
 * What the types buy: `input` is the def's Zod-inferred body (required exactly when
 * the route declares one), `query` likewise, `params` keys are parsed from the path
 * template literal (`:id` → `{ id: string }`, required only when present), and the
 * resolved value is the handler's return type. A server shape change breaks the
 * page at COMPILE time instead of at runtime — the drift class this kills.
 *
 * The method/path strings are restated here (values can't cross a type-only
 * import); they live on the one line adjacent to the def reference, and a future
 * slice can generate these files from the defs wholesale.
 *
 * The double call — typedRoute<Def>()(method, path) — exists because TypeScript
 * has no partial type-argument inference: Def is explicit, the path literal is
 * inferred (it drives the `params` typing).
 */
import { apiFetch } from "./index.ts";

/** Structural match for @d12labs/server's RouteDef — type-only, no dependency. */
export type RouteDefLike = {
	method: string;
	path: string;
	// biome-ignore lint/suspicious/noExplicitAny: contravariant match for any handler context
	handler: (ctx: any) => unknown;
	input?: { parse: (raw: unknown) => unknown };
	query?: { parse: (raw: unknown) => unknown };
};

/**
 * What the CALLER sends — the schema's INPUT side, so Zod `.default()` /
 * `.transform()` fields stay optional/pre-transform on the wire type. Zod 4
 * exposes it structurally via the `_zod.input` phantom (type-only, erased);
 * a plain `{ parse }` validator without it falls back to the output type.
 */
type SchemaIn<S> = [NonNullable<S>] extends [never]
	? undefined
	: NonNullable<S> extends { _zod: { input: infer I } }
		? I
		: NonNullable<S> extends { parse: (raw: unknown) => infer T }
			? T
			: undefined;
type InputOf<D extends RouteDefLike> = SchemaIn<D["input"]>;
type QueryOf<D extends RouteDefLike> = SchemaIn<D["query"]>;
/** What the route resolves to — the handler's awaited return, minus raw `Response`s. */
export type RouteOutputOf<D extends RouteDefLike> = Exclude<
	Awaited<ReturnType<D["handler"]>>,
	Response
>;

/** Param names in a path template: "/a/:id/b/:key" → "id" | "key". */
type ParamNames<P extends string> = P extends `${string}:${infer Rest}`
	? Rest extends `${infer Name}/${infer Tail}`
		? Name | ParamNames<`/${Tail}`>
		: Rest
	: never;

type ParamsField<P extends string> = [ParamNames<P>] extends [never]
	? { params?: undefined }
	: { params: Record<ParamNames<P>, string> };

type InputField<D extends RouteDefLike> =
	undefined extends InputOf<D> ? { input?: undefined } : { input: InputOf<D> };

type QueryField<D extends RouteDefLike> =
	undefined extends QueryOf<D>
		? { query?: Record<string, string | number | boolean> }
		: { query: QueryOf<D> };

export type TypedCallOpts<D extends RouteDefLike, P extends string> = ParamsField<P> &
	InputField<D> &
	QueryField<D>;

type HasRequiredOpts<D extends RouteDefLike, P extends string> = [ParamNames<P>] extends [never]
	? undefined extends InputOf<D>
		? false
		: true
	: true;

export type TypedRouteFn<D extends RouteDefLike, P extends string> =
	HasRequiredOpts<D, P> extends true
		? (opts: TypedCallOpts<D, P>) => Promise<RouteOutputOf<D>>
		: (opts?: TypedCallOpts<D, P>) => Promise<RouteOutputOf<D>>;

/**
 * Bind one server route def (as a type) to its method + path (as values).
 * Returns the typed call function described above.
 */
export function typedRoute<D extends RouteDefLike>() {
	return <P extends string>(method: D["method"], path: P): TypedRouteFn<D, P> => {
		const fn = async (opts?: {
			params?: Record<string, string>;
			input?: unknown;
			query?: Record<string, unknown>;
		}): Promise<RouteOutputOf<D>> => {
			let url: string = path;
			if (opts?.params) {
				for (const [key, value] of Object.entries(opts.params)) {
					url = url.replace(`:${key}`, encodeURIComponent(value));
				}
			}
			if (opts?.query) {
				const qs = new URLSearchParams();
				for (const [key, value] of Object.entries(opts.query)) {
					if (value !== undefined && value !== null) qs.set(key, String(value));
				}
				const encoded = qs.toString();
				if (encoded) url += `?${encoded}`;
			}
			return apiFetch<RouteOutputOf<D>>(url, {
				method,
				body: opts?.input !== undefined ? JSON.stringify(opts.input) : undefined,
			});
		};
		return fn as TypedRouteFn<D, P>;
	};
}
